import useCalendar from "../../calendar/context/useCalendar";
import { useDispatch, useSelector } from "../../store/store";
import {
    getCalendarData,
    getOriginalData,
    initData,
    setShowChangeList,
    isRegularChanged,
    setCalendarData,
} from "../../store/data";
import {
    Consultation,
    IndividualWeekSettings,
    Week,
} from "../../calendar/model";
import _set from "lodash/set";
import { useTranslation } from "react-i18next";
import {
    arraysEqual,
    filterObjectByKey,
    isEven,
    getDateFromWeek,
    getCurrentWeek,
    has53Weeks,
    getEvenOdd,
    isEmptyObject,
    hasKey,
    filterWeekSettingsData,
    cleanRegularWeekSettings,
} from "../utils";

const sort = (week: Week) => {
    const sorted: Week = {};

    Object.keys(week).forEach((day) => {
        sorted[day] = week[parseInt(day)]
            ?.slice()
            .sort((a: Consultation, b: Consultation) => {
                if (a.start < b.start) {
                    return -1;
                }
                if (a.start > b.start) {
                    return 1;
                }

                return 0;
            });
    });

    return sorted;
};

const useSaveWeekSettingsChanges = (closeModal: any, isRegular: boolean) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const calendarData = useSelector(getCalendarData);
    const originalData = useSelector(getOriginalData);

    const { inputCallbacks } = useCalendar();

    return () => {
        const copyRegulars = (regularSettings: any) => {
            let individualSettings: any = {};

            const currentWeek = getCurrentWeek();
            const currentYear = new Date().getFullYear();

            let _weekNumbers = 52;

            if (has53Weeks(currentYear)) {
                _weekNumbers = 53;
            }

            for (let week = 1; week <= _weekNumbers; week++) {
                const year =
                    week >= currentWeek ? currentYear : currentYear + 1;

                if (!individualSettings[year]) {
                    individualSettings[year] = {};
                }
                if (regularSettings.oddWeekUsed) {
                    if (getEvenOdd(week) == "even") {
                        individualSettings[year][week] = sort(
                            regularSettings["even"]
                        );
                    } else {
                        individualSettings[year][week] = sort(
                            regularSettings["odd"]
                        );
                    }
                } else {
                    individualSettings[year][week] = sort(
                        regularSettings["even"]
                    );
                }
            }
            return individualSettings;
        };

        // Sorting consultations before submitting

        const sortIndividual = (individualWeekSettings: any) => {
            let newIndividual: any = {};
            Object.keys(individualWeekSettings).forEach((year) => {
                if (!newIndividual[year]) {
                    newIndividual[year] = {};
                }

                Object.keys(individualWeekSettings[year]).forEach((week) => {
                    newIndividual[year][week] = sort(
                        individualWeekSettings[year][week]
                    );
                });
            });
            return newIndividual;
        };

        let newData = {
            ...calendarData,
            individualWeekSettings: sortIndividual(
                isRegular
                    ? copyRegulars(calendarData.regularWeekSettings)
                    : calendarData.individualWeekSettings
            ),
            regularWeekSettings: {
                ...calendarData.regularWeekSettings,
                even: sort(calendarData.regularWeekSettings.even),
                odd: sort(calendarData.regularWeekSettings.odd),
            },
        };

        dispatch(setCalendarData(newData));
        // dispatch(initData(newData));

        let isTimeInvalid = false;
        Object.keys(newData.individualWeekSettings).forEach((week) => {
            Object.keys(newData.individualWeekSettings[week]).forEach((day) => {
                Object.values(
                    newData.individualWeekSettings[week][day]
                ).forEach((time: any) => {
                    if (time) {
                        time.forEach((consultationTime: any) => {
                            if (consultationTime) {
                                const [startHour, startMinute] =
                                    consultationTime.start
                                        .split(":")
                                        .map(Number);
                                const [endHour, endMinute] =
                                    consultationTime.end.split(":").map(Number);

                                if (
                                    startMinute % 5 !== 0 ||
                                    endMinute % 5 !== 0
                                ) {
                                    isTimeInvalid = true;
                                    return;
                                }
                            }
                        });
                    }
                });
            });
        });

        const checkDifference = (regularDayData: any, dayData: any) => {
            if (!dayData) {
                return false;
            }
            let difference = false;
            if (regularDayData.length != dayData.length) {
                difference = true;
            } else {
                regularDayData.forEach((rd: any, index: number) => {
                    let _dayData = dayData[index];

                    if (
                        rd.end != _dayData.end ||
                        rd.start != _dayData.start ||
                        rd.room_id != _dayData.room_id
                    ) {
                        difference = true;
                    }
                    if (
                        !arraysEqual(
                            rd.specialization_id,
                            _dayData.specialization_id
                        )
                    ) {
                        difference = true;
                    }
                });
            }
            return difference;
        };

        const getCheckData = (individualWeekSettings: any) => {
            function getISOWeekNumber(date: any) {
                const target = new Date(date);
                target.setDate(
                    target.getDate() - ((target.getDay() + 6) % 7) + 3
                ); // Move to the nearest Thursday
                const firstThursday = new Date(target.getFullYear(), 0, 4);
                const diff = target.getTime() - firstThursday.getTime();
                const weekNumber = 1 + Math.round(diff / (7 * 86400000));
                return weekNumber;
            }

            const getCurrentWeek = function (date = new Date()) {
                return getISOWeekNumber(date);
            };

            let currentYear = new Date().getFullYear();
            let currentWeek = getCurrentWeek();

            let _individualWeekSettings = individualWeekSettings;

            let thisYearData: any = {};
            let nextYearData: any = {};
            let thisYearWeeksNumber: any;

            for (const [index, year] of Object.entries(
                _individualWeekSettings
            )) {
                if (index.toString() == currentYear.toString()) {
                    let yearData = _individualWeekSettings[`${index}`];

                    for (let week in yearData) {
                        let _weekData = yearData[week];

                        let dayOfWeek = new Date().getDay();
                        let actualDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

                        if (week.toString() == currentWeek.toString()) {
                            _weekData = filterObjectByKey(_weekData, actualDay);
                        }

                        if (+week >= currentWeek) {
                            thisYearData[week] = _weekData;
                        }
                    }
                }
                thisYearWeeksNumber = Object.keys(thisYearData).length;

                if (index.toString() == (currentYear + 1).toString()) {
                    let yearData = _individualWeekSettings[`${index}`];

                    let nextYearWeeksNumber = 52 - thisYearWeeksNumber;

                    for (let week in yearData) {
                        if (+week <= nextYearWeeksNumber) {
                            nextYearData[week] = yearData[week];
                        }
                    }
                }
            }

            let updatedIndividualWeekSettings: any = {};
            updatedIndividualWeekSettings[currentYear] = thisYearData;
            updatedIndividualWeekSettings[currentYear + 1] = nextYearData;

            // return updatedIndividualWeekSettings;

            let newIndividual: any = {};
            Object.keys(updatedIndividualWeekSettings).forEach((year) => {
                if (!newIndividual[year]) {
                    newIndividual[year] = {};
                }

                Object.keys(updatedIndividualWeekSettings[year]).forEach(
                    (week) => {
                        newIndividual[year][week] = sort(
                            updatedIndividualWeekSettings[year][week]
                        );
                    }
                );
            });

            return newIndividual;
        };

        let checkData = getCheckData(originalData.individualWeekSettings);
        let modifiedData = getCheckData(newData.individualWeekSettings);

        let listData: any = [];

        if (isRegular && checkData && modifiedData) {
            let dataToShow = [];

            let regularSettings = {
                ...calendarData.regularWeekSettings,
                even: sort(calendarData.regularWeekSettings.even),
                odd: sort(calendarData.regularWeekSettings.odd),
            };

            for (const year in modifiedData) {
                const weeks = modifiedData[year];
                for (const week in weeks) {
                    const days = weeks[week];

                    if (isEmptyObject(days)) {
                        if (checkData[year][week]) {
                            let _days = checkData[year][week];

                            if (!_days) {
                                return;
                            }

                            for (const day in _days) {
                                dataToShow.push({
                                    id: `${year}-${week}-${day}`,
                                    date: getDateFromWeek(+year, +week, +day),
                                    year: year,
                                    week: week,
                                    day: day,
                                    data: checkData[year][week][day],
                                });
                            }
                        }
                    } else {
                        let _days = checkData[year][week];
                        if (_days) {
                            for (const _day in _days) {
                                if (!hasKey(days, _day)) {
                                    dataToShow.push({
                                        id: `${year}-${week}-${_day}`,
                                        date: getDateFromWeek(
                                            +year,
                                            +week,
                                            +_day
                                        ),
                                        year: year,
                                        week: week,
                                        day: _day,
                                        data: checkData[year][week][_day],
                                    });
                                }
                            }
                        }
                    }

                    for (const day in days) {
                        const dayData = days[day];

                        let _data: any = {};

                        let regularDayData = regularSettings.even[day];
                        if (regularSettings.oddWeekUsed) {
                            if (!isEven(+week)) {
                                regularDayData = regularSettings.odd[day];
                            }
                        }

                        if (
                            !checkData[year][week] ||
                            !checkData[year][week][day]
                        ) {
                            _data = {
                                id: `${year}-${week}-${day}`,
                                date: getDateFromWeek(+year, +week, +day),
                                year: year,
                                week: week,
                                day: day,
                                data: dayData,
                            };
                            dataToShow.push(_data);
                        } else {
                            _data = {
                                id: `${year}-${week}-${day}`,
                                date: getDateFromWeek(+year, +week, +day),
                                year: year,
                                week: week,
                                day: day,
                                data: checkData[year][week][day],
                            };

                            if (!regularDayData) {
                                if (_data.data) {
                                    dataToShow.push({
                                        ..._data,
                                        emptyOnChange: true,
                                    });
                                }
                            } else {
                                if (
                                    checkDifference(regularDayData, _data.data)
                                ) {
                                    if (_data.data) {
                                        dataToShow.push(_data);
                                    }
                                }
                            }
                        }
                    }
                }
            }
            listData = dataToShow;
        }

        if (isTimeInvalid) {
            if (inputCallbacks.onStartEndTimeError) {
                inputCallbacks.onStartEndTimeError();
            } else {
                alert(t("start_end_time_error"));
            }
            return false;
        }

        // TODO check regular settings time too

        // console.log("listData", listData);

        if (isRegular && listData.length > 0) {
            dispatch(setShowChangeList(true));
        } else {
            // Filter newData to only include current and next year with proper week limits
            const filteredData = filterWeekSettingsData(
                newData.individualWeekSettings
            );

            // Create a new object instead of modifying the existing one
            const filteredNewData = {
                ...newData,
                individualWeekSettings: filteredData,
            };

            // const cleanedData = cleanRegularWeekSettings(filteredNewData);

            dispatch(initData(filteredNewData));
            void inputCallbacks.onSaveCalendar(filteredNewData);
            closeModal();
        }
    };
};

export default useSaveWeekSettingsChanges;
